import React from 'react';
import SkeletonBlock from '../ui/SkeletonBlock';
import { Card, CardContent } from '../ui';
import {
  DASHBOARD_CHART_BODY,
  DASHBOARD_CHART_CARD,
  DASHBOARD_CHART_CELL,
  DASHBOARD_CHART_CONTENT,
  DASHBOARD_CHART_GRID,
  DASHBOARD_CHART_HEADER,
} from '../admin/dashboardChartCardClasses';

/** Stat tile placeholder — matches StatTile compact default; pass size="default" for large. */
export function StatTileSkeleton({ size = 'compact' }: { size?: 'default' | 'compact' }) {
  const isCompact = size === 'compact';
  return (
    <div
      className={`flex h-full flex-col justify-between border border-primary-500/10 bg-white/90 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)] backdrop-blur-xl ${
        isCompact
          ? 'min-h-[72px] rounded-xl p-2.5 sm:p-3'
          : 'min-h-[100px] rounded-2xl p-4 sm:p-5'
      }`}
    >
      <div className={`flex items-start justify-between ${isCompact ? 'mb-1' : 'mb-3'}`}>
        <SkeletonBlock className={isCompact ? 'h-2.5 w-16' : 'h-3 w-20'} />
        <SkeletonBlock
          className={isCompact ? 'h-5 w-5 rounded-md' : 'h-8 w-8 rounded-xl'}
        />
      </div>
      <div>
        <SkeletonBlock
          className={
            isCompact
              ? 'mb-1 h-6 w-[2.75rem] rounded sm:h-7 sm:w-14'
              : 'mb-2 h-8 w-16 rounded'
          }
        />
        <SkeletonBlock className={isCompact ? 'h-2.5 w-20' : 'h-3 w-24'} />
      </div>
    </div>
  );
}

export function DashboardChartHeaderSkeleton({ showAction = false }: { showAction?: boolean }) {
  return (
    <div className={`mb-3 ${DASHBOARD_CHART_HEADER}`}>
      <div className="flex min-w-0 items-center gap-2.5">
        <SkeletonBlock className="h-7 w-7 flex-shrink-0 rounded-md" />
        <div className="min-w-0 space-y-1.5">
          <SkeletonBlock className="h-4 w-32" />
          <SkeletonBlock className="h-2.5 w-40" />
        </div>
      </div>
      {showAction ? <SkeletonBlock className="h-7 w-16 flex-shrink-0 rounded-md" /> : null}
    </div>
  );
}

/** Donut ring + center labels + legend rows — matches RevenueWidget / SectorsWidget body. */
export function DonutLegendBodySkeleton({
  legendRows = 5,
  showFooter = false,
}: {
  legendRows?: number;
  showFooter?: boolean;
}) {
  return (
    <>
      <div className={`${DASHBOARD_CHART_BODY} flex flex-col items-center gap-3 xl:flex-row`}>
        <div className="relative h-[160px] w-[160px] flex-shrink-0 sm:h-[180px] sm:w-[180px]">
          <SkeletonBlock className="h-full w-full rounded-full" />
          <div className="absolute inset-[16px] rounded-full bg-white sm:inset-[18px]" aria-hidden />
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 px-4">
            <SkeletonBlock className="h-5 w-20 rounded" />
            <SkeletonBlock className="h-2 w-14 rounded" />
          </div>
        </div>
        <div className="w-full min-w-0 flex-1 space-y-1">
          {Array.from({ length: legendRows }).map((_, i) => (
            <div
              key={i}
              className="grid grid-cols-[8px_1fr_64px_28px] items-center gap-2 px-1 py-0.5"
            >
              <SkeletonBlock className="h-2 w-2 rounded-sm" />
              <SkeletonBlock className="h-2.5" />
              <SkeletonBlock className="h-2.5" />
              <SkeletonBlock className="h-2.5" />
            </div>
          ))}
        </div>
      </div>
      {showFooter ? (
        <div className="mt-auto hidden flex-shrink-0 grid-cols-3 gap-2 border-t border-primary-500/10 pt-3 sm:grid sm:gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-1 text-center">
              <SkeletonBlock className="mx-auto h-2.5 w-14" />
              <SkeletonBlock className="mx-auto h-3.5 w-16" />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

/** Horizontal bar rows — matches RegionsWidget body. */
export function RegionsBarsBodySkeleton({ rowCount = 6 }: { rowCount?: number }) {
  return (
    <div className={`${DASHBOARD_CHART_BODY} flex flex-col justify-center space-y-2.5 py-1`}>
      {Array.from({ length: rowCount }).map((_, i) => (
        <div key={i} className="flex items-center gap-2.5">
          <SkeletonBlock className="h-3.5 w-20 flex-shrink-0 sm:w-28" />
          <SkeletonBlock className="h-4 flex-1 rounded" />
          <SkeletonBlock className="h-3.5 w-10 flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}

/** Growth chart body — optional header when parent already renders live title. */
export function ClientGrowthChartBodySkeleton({ omitHeader = false }: { omitHeader?: boolean }) {
  return (
    <div className="flex-1">
      {!omitHeader ? (
        <div className={`mb-3 ${DASHBOARD_CHART_HEADER}`}>
          <div className="flex items-center gap-2.5">
            <SkeletonBlock className="h-7 w-7 rounded-md" />
            <div className="space-y-1.5">
              <SkeletonBlock className="h-4 w-40" />
              <SkeletonBlock className="h-2.5 w-24" />
            </div>
          </div>
          <SkeletonBlock className="hidden h-10 w-20 rounded-lg sm:block" />
        </div>
      ) : null}
      <div className="mb-3 flex gap-1.5">
        <SkeletonBlock className="h-7 flex-1 rounded-md sm:w-11 sm:flex-initial" />
        <SkeletonBlock className="h-7 flex-1 rounded-md sm:w-11 sm:flex-initial" />
        <SkeletonBlock className="h-7 flex-1 rounded-md sm:w-11 sm:flex-initial" />
      </div>
      <div className="mb-3 rounded-lg border border-primary-500/10 p-2.5 sm:p-3">
        <div className="flex items-start gap-2.5">
          <SkeletonBlock className="h-4 w-4 flex-shrink-0 rounded" />
          <div className="flex-1 space-y-1.5">
            <SkeletonBlock className="h-2.5 w-3/4" />
            <SkeletonBlock className="h-2.5 w-1/2" />
          </div>
        </div>
      </div>
      <SkeletonBlock className="h-52 w-full rounded-xl sm:h-56" />
    </div>
  );
}

/** List widget rows — Latest Pipeline / Recent Clients cards. */
export function DashboardListRowsSkeleton({ rowCount = 5 }: { rowCount?: number }) {
  return (
    <ul className="divide-y divide-primary-500/10" aria-hidden>
      {Array.from({ length: rowCount }).map((_, i) => (
        <li
          key={i}
          className="flex flex-col gap-2 px-1.5 py-2.5 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-center gap-2.5">
            <SkeletonBlock className="h-8 w-8 flex-shrink-0 rounded-lg" />
            <div className="space-y-1.5">
              <SkeletonBlock className="h-3.5 w-28" />
              <SkeletonBlock className="h-2.5 w-20" />
            </div>
          </div>
          <SkeletonBlock className="h-7 w-16 rounded-md" />
        </li>
      ))}
    </ul>
  );
}

function ChartCardShell({ children }: { children: React.ReactNode }) {
  return (
    <div className={DASHBOARD_CHART_CELL}>
      <Card variant="premium" padding="none" className={DASHBOARD_CHART_CARD}>
        <CardContent className={DASHBOARD_CHART_CONTENT}>{children}</CardContent>
      </Card>
    </div>
  );
}

export function DashboardGrowthChartCardSkeleton() {
  return (
    <ChartCardShell>
      <ClientGrowthChartBodySkeleton />
    </ChartCardShell>
  );
}

export function DashboardRevenueCardSkeleton() {
  return (
    <ChartCardShell>
      <DashboardChartHeaderSkeleton showAction />
      <DonutLegendBodySkeleton legendRows={5} showFooter />
    </ChartCardShell>
  );
}

export function DashboardSectorsCardSkeleton() {
  return (
    <ChartCardShell>
      <DashboardChartHeaderSkeleton />
      <DonutLegendBodySkeleton legendRows={6} />
    </ChartCardShell>
  );
}

export function DashboardRegionsCardSkeleton() {
  return (
    <ChartCardShell>
      <DashboardChartHeaderSkeleton />
      <RegionsBarsBodySkeleton />
    </ChartCardShell>
  );
}

/** Route-level admin dashboard skeleton — mirrors live /admin layout. */
export function AdminDashboardLayoutSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6 lg:px-8">
        <SkeletonBlock className="mb-4 h-5 w-48 rounded-lg" />

        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <StatTileSkeleton key={i} size="compact" />
          ))}
        </div>

        <div className={DASHBOARD_CHART_GRID}>
          <DashboardGrowthChartCardSkeleton />
          <DashboardRevenueCardSkeleton />
          <DashboardSectorsCardSkeleton />
          <DashboardRegionsCardSkeleton />
        </div>

        <div className="mb-6 grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-2">
          <Card variant="premium" padding="none" className="h-full">
            <CardContent className="p-3 sm:p-4">
              <div className="mb-2.5 flex items-center justify-between">
                <SkeletonBlock className="h-4 w-32" />
                <SkeletonBlock className="h-3 w-12" />
              </div>
              <DashboardListRowsSkeleton />
            </CardContent>
          </Card>
          <Card variant="premium" padding="none" className="h-full">
            <CardContent className="p-3 sm:p-4">
              <div className="mb-2.5 flex items-center justify-between">
                <SkeletonBlock className="h-4 w-36" />
                <SkeletonBlock className="h-3 w-12" />
              </div>
              <DashboardListRowsSkeleton />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
